import { Alert, Box, Button, Snackbar, Stack } from '@mui/material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import GetAppIcon from '@mui/icons-material/GetApp';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import { usePWA } from '@/hooks/usePWA';
import { useState } from 'react';

export function PWAInstallPrompt() {
  const { isInstallable, isInstalled, isOnline, hasUpdate, isUpdating, install, update } = usePWA();
  const [installError, setInstallError] = useState<string | null>(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(true);
  const [showUpdatePrompt, setShowUpdatePrompt] = useState(true);

  const handleInstall = async () => {
    try {
      setInstallError(null);
      await install();
      setShowInstallPrompt(false);
    } catch (error) {
      setInstallError(error instanceof Error ? error.message : 'Failed to install application');
    }
  };

  const handleUpdate = () => {
    update();
  };

  return (
    <>
      {/* Install Prompt */}
      {isInstallable && !isInstalled && showInstallPrompt && (
        <Snackbar
          open
          autoHideDuration={null}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          onClose={() => setShowInstallPrompt(false)}
        >
          <Alert
            onClose={() => setShowInstallPrompt(false)}
            severity="info"
            icon={<GetAppIcon />}
            sx={{ width: '100%', gap: 2 }}
            action={
              <Stack direction="row" spacing={1}>
                <Button
                  color="inherit"
                  size="small"
                  onClick={handleInstall}
                  startIcon={<GetAppIcon />}
                >
                  Instalar
                </Button>
                <Button color="inherit" size="small" onClick={() => setShowInstallPrompt(false)}>
                  No, gracias
                </Button>
              </Stack>
            }
          >
            Instala Remote Assist para acceder sin conexión
          </Alert>
        </Snackbar>
      )}

      {/* Install Error */}
      {installError && (
        <Snackbar
          open
          autoHideDuration={5000}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          onClose={() => setInstallError(null)}
        >
          <Alert onClose={() => setInstallError(null)} severity="error" sx={{ width: '100%' }}>
            {installError}
          </Alert>
        </Snackbar>
      )}

      {/* Update Available */}
      {hasUpdate && !isUpdating && showUpdatePrompt && (
        <Snackbar
          open
          autoHideDuration={null}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          onClose={() => setShowUpdatePrompt(false)}
        >
          <Alert
            onClose={() => setShowUpdatePrompt(false)}
            severity="warning"
            icon={<CloudDownloadIcon />}
            sx={{ width: '100%', gap: 2 }}
            action={
              <Stack direction="row" spacing={1}>
                <Button
                  color="inherit"
                  size="small"
                  onClick={handleUpdate}
                  startIcon={<CloudDownloadIcon />}
                  disabled={isUpdating}
                >
                  Actualizar
                </Button>
                <Button color="inherit" size="small" onClick={() => setShowUpdatePrompt(false)}>
                  Luego
                </Button>
              </Stack>
            }
          >
            Hay una nueva versión disponible
          </Alert>
        </Snackbar>
      )}

      {/* Offline Status */}
      {!isOnline && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 1200,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            padding: '8px 16px',
            backgroundColor: 'warning.main',
            color: 'warning.contrastText',
            borderRadius: 1,
          }}
        >
          <CloudOffIcon fontSize="small" />
          <span>Sin conexión</span>
        </Box>
      )}

      {/* Online Status (show briefly when coming back online) */}
      {isOnline && isInstalled && (
        <Snackbar
          open
          autoHideDuration={2000}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert severity="success" icon={<CloudDoneIcon />} sx={{ width: '100%' }}>
            Conectado
          </Alert>
        </Snackbar>
      )}
    </>
  );
}
